const { doesStringContainAny, getElements } = window.BAO.Utils

/**
 * A Shopify Image product
 * @typedef {Object} ShopifyImage
 *
 * @property {string} [alt]
 * @property {number} aspect_ratio
 * @property {number} height
 * @property {number} id
 * @property {('image'|'model'|'video'|'external_video')} media_type
 * @property {number} position
 */

/**
 * A Shopify Media product
 * @typedef {Object} ShopifyMedia
 *
 * @property {string|null} alt
 * @property {number} aspect_ratio
 * @property {number} height
 * @property {number} id
 * @property {('image'|'model'|'video'|'external_video')} media_type
 * @property {number} position
 * @property {ShopifyImage} preview_image
 * @property {string} src
 * @property {number} width
 */

/**
 * A Shopify Variant product
 * @typedef {Object} ShopifyVariant
 *
 * @property {boolean} available
 * @property {string|null} barcode
 * @property {number|string} compare_at_price
 * @property {string} featured_image
 * @property {object} [featured_media]
 * @property {number} id
 * @property {string} inventory_management
 * @property {string} name
 * @property {string} option1
 * @property {string} option2
 * @property {string} option3
 * @property {string[]} options
 * @property {number|string} price
 * @property {string} public_title
 * @property {boolean} requires_shipping
 * @property {string} sku
 * @property {boolean} taxable
 * @property {string} title
 * @property {number} weight
 */

/**
 * A Shopify product
 * @typedef {Object} ShopifyProduct
 *
 * @property {boolean} available
 * @property {number|string} compare_at_price
 * @property {number|string} compare_at_price_max
 * @property {number|string} compare_at_price_min
 * @property {boolean} compare_at_price_varies
 * @property {string} content
 * @property {string} created_at
 * @property {string} description
 * @property {string} featured_image
 * @property {string} handle
 * @property {number} id
 * @property {string[]} images
 * @property {ShopifyMedia[]} media
 * @property {string[]} options
 * @property {number|string} price
 * @property {number|string} price_max
 * @property {number|string} price_min
 * @property {boolean} price_varies
 * @property {string} published_at
 * @property {string[]} tags
 * @property {string} title
 * @property {string|null} type
 * @property {ShopifyVariant[]} variants
 * @property {string|null} vendor
 */

/**
 * This ensures all links to external sites are _blank'd and have the relevant rel properties set
 */
function externalLinks () {
  const links = getElements('a')

  // If the link starts with any of these things, we'll open a 600x300 popup
  // window for them.
  const usePopupPrefixes = [
    'https://twitter.com/intent/tweet?',
    'https://www.facebook.com/sharer.php?',
    'https://www.linkedin.com/shareArticle?',
  ]

  const thisDomain =
    window.location.hostname.indexOf('www.') === 0
      ? window.location.hostname.substr(4)
      : window.location.hostname

  links.forEach((link) => {
    const href = link.getAttribute('href')

    if (
      !href ||
      href.indexOf('mailto:') >= 0 ||
      doesStringContainAny(href, ['http://', 'https://', '//']) === false
    ) {
      return
    }

    const domain =
      link.hostname.indexOf('www.') === 0
        ? link.hostname.substr(4)
        : link.hostname

    if (domain !== thisDomain) {
      link.setAttribute('target', '_blank')
      // Add noopener and 'noreferrer' to work around this:
      // https://dev.to/ben/the-targetblank-vulnerability-by-example
      let rel = link.getAttribute('rel')

      if (!rel) {
        // will be null if it is not set
        rel = ''
      }

      link.setAttribute('rel', `${rel} noopener noreferrer`)
    }

    // Use a popup for social sharing links.
    // eslint-disable-next-line no-unused-vars
    for (const prefix of usePopupPrefixes) {
      if (href.indexOf(prefix) === 0) {
        link.addEventListener('click', (event) => {
          event.preventDefault()
          window.open(
            href,
            '_blank',
            'width=600,height=300,menubar=0,toolbar=0,status=0',
          )
        })

        break
      }
    }
  })
}
externalLinks()

const body = document.body || document.documentElement
body.classList.remove('util-IsTabbing')

const handleFirstTab = (e) => {
  if (e.keyCode === 9) {
    body.classList.add('util-IsTabbing')

    window.removeEventListener('keydown', handleFirstTab)
    window.addEventListener('mousedown', handleMouseDownOnce)
  }
}

window.addEventListener('keydown', handleFirstTab)

const handleMouseDownOnce = () => {
  body.classList.remove('util-IsTabbing')

  window.removeEventListener('mousedown', handleMouseDownOnce)
  window.addEventListener('keydown', handleFirstTab)
}

window.addEventListener('mousedown', handleMouseDownOnce)

const disableBlankSearchOnHeaderForMobile = () => {
  document.addEventListener('submit', (e) => {
    const form = e.target
    if (!form?.classList.contains('hd-SearchDropdown_Form')) {
      return
    }

    const searchInput = form.querySelector('input[name="q"]')
    if (!searchInput) {
      return
    }

    searchInput.setCustomValidity('')

    if (!searchInput.value.trim()) {
      e.preventDefault()
      searchInput.value = ''
      searchInput.setCustomValidity('Please fill out this field.')
      searchInput.reportValidity()
    }
  })
  document.addEventListener('input', (e) => {
    const isSearchInput =
      e.target.name === 'q' &&
      e.target.classList.contains('hd-SearchDropdown_Input')
    if (isSearchInput) {
      e.target.setCustomValidity('')
    }
  })
}

disableBlankSearchOnHeaderForMobile()

const getRegistryTrackingProductInfo = (trigger) => {
  return (
    trigger.closest('product-info') ||
    document.querySelector('product-info[data-cnstrc-product-detail]') ||
    document.querySelector('product-info')
  )
}

const getRegistryTrackingData = (trigger) => {
  const productInfo = getRegistryTrackingProductInfo(trigger)

  if (!productInfo) {
    return null
  }

  const itemId =
    productInfo.getAttribute('data-cnstrc-item-id') ||
    productInfo.dataset.productId
  const variationId =
    productInfo.getAttribute('data-cnstrc-item-variation-id') ||
    productInfo.querySelector('input[name="id"]')?.value

  if (!itemId || !variationId) {
    return null
  }

  return {
    itemId,
    variationId,
  }
}

// Shared Constructor wishlist conversion helper
function trackConstructorWishlistConversion (itemId, variationId) {
  if (!window.ConstructorioTracker || !itemId || !variationId) return
  window.ConstructorioTracker.trackConversion('add_to_wishlist', {
    item_id: String(itemId),
    variation_id: String(variationId),
  })
}
window.trackConstructorWishlistConversion = trackConstructorWishlistConversion

const trackRegistryConversion = (event) => {
  const trigger =
    event.target.closest('[data-cnstrc-registry-provider="babylist"]') ||
    event.target.closest('#swym-registry-add-modal-btn')
  if (!trigger) return
  const trackingData = getRegistryTrackingData(trigger)
  if (!trackingData) return
  trackConstructorWishlistConversion(
    trackingData.itemId,
    trackingData.variationId,
  )
}

const registryTrigger = document.querySelector(
  '[data-cnstrc-registry-provider="babylist"], #swym-registry-add-modal-btn',
)
if (registryTrigger) {
  document.addEventListener('click', trackRegistryConversion)
}

document.addEventListener('click', (e) => {
  const link = e.target.closest('.pgn-LoadMore_BackTop')
  if (!link) return
  const target = document.getElementById('SkipToContent')
  if (!target) return
  e.preventDefault()
  window.scrollTo({ top: 0 })
  target.focus()
})
